import { ApikeyEffects } from './store/APIKey/apikey_effect';
import { APP_INITIALIZER } from '@angular/core';
import { AppComponent } from './app.component';
import { ApplicationEffects } from './store/Jobs/jobs_effect';
import { AppRoutingModule } from './app-routing.module';
import { AuthenticationService } from './core/services/auth.service';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { ComponentAccessUserService } from './core/services/utelize/system/component-access/component-access-user-service';
import config from 'devextreme/core/config';
import { ConfigSystemParams } from './core/consts';
import { ConfigSystemService } from './core/services/utelize/system/config-system/config-system-service';
import { CRMEffects } from './store/CRM/crm_effect';
import { CryptoEffects } from './store/Crypto/crypto_effect';
import { DATE_PIPE_DEFAULT_OPTIONS, DecimalPipe } from '@angular/common';
import { EcommerceEffects } from './store/Ecommerce/ecommerce_effect';
import { EffectsModule } from '@ngrx/effects';
import { EncryptionService } from './core/services/utelize/general/encryption-service';
import { environment } from '../environments/environment';
import { ErrorInterceptor } from './core/helpers/error.interceptor';
import { FileManagerEffects } from './store/File Manager/filemanager_effect';
import { firstValueFrom } from 'rxjs';
import { getMsalConfig } from './core/_config/auth-config';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { InvoiceEffects } from './store/Invoice/invoice_effect';
import { JwtInterceptor } from './core/helpers/jwt.interceptor';
import { LayoutsModule } from "./layouts/layouts.module";
import { licenseKey } from 'src/devextreme-license';
import { MsalModule } from '@azure/msal-angular';
import { NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { NgModule } from '@angular/core';
import { NgPipesModule } from 'ngx-pipes';
import { PagesModule } from "./pages/pages.module";
import { PlatformLandingComponent } from './platform/platform-landing.component';
import { ProjectEffects } from './store/Project/project_effect';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { PublicClientApplication, InteractionType } from '@azure/msal-browser';
import { RiskIntelligenceEffects } from './store/RiskIntelligence/risk_intelligence_effect';
import { rootReducer } from './store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreModule } from '@ngrx/store';
import { TaskEffects } from './store/Task/task_effect';
import { TicketEffects } from './store/Ticket/ticket_effect';
import { ToastsContainer } from './shared/toast/toasts-container.component';
import { TodoEffects } from './store/Todo/todo_effect';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { VersionService } from './core/services/utelize/general/version-service';

export function createTranslateLoader(httpClient: HttpClient, versionService: VersionService): TranslateHttpLoader {
  return new TranslateHttpLoader(httpClient, 'assets/i18n/', `.json?v=${encodeURIComponent(versionService.version)}`);
}

export function loadAccessGuids(authenticationService: AuthenticationService, componentAccessUserService: ComponentAccessUserService) {
  return async () => {
    try {
      //CRN_20260527 - Also loaded in LoginComponent.getUserAccessGuids() - initial get after login
      
      const curUser=authenticationService?.currentUserObject();

      if (!curUser || curUser.companyId<=0 || curUser.userId<=0) {
        componentAccessUserService.userAccessGuids=undefined;
        return;
      }

      componentAccessUserService.userAccessGuids=await firstValueFrom(componentAccessUserService.getAccessGuids(curUser.companyId, curUser.userId));
    }
    catch {
      componentAccessUserService.userAccessGuids=undefined;
    }
  };
}

export function loadConfigSystem(configSystemService: ConfigSystemService, encryptionService: EncryptionService) {
  return async () => {
    try {
      await firstValueFrom(configSystemService.refreshConfigSystemValues());
      encryptionService.serverSideEncryption         =configSystemService.getValueAsBoolean(ConfigSystemParams.SecurityServerSideEncryption,          true);
      encryptionService.serverSideEncryptionDashboard=configSystemService.getValueAsBoolean(ConfigSystemParams.SecurityServerSideEncryptionDashboard, true);
      encryptionService.serverSideEncryptionWidgets  =configSystemService.getValueAsBoolean(ConfigSystemParams.SecurityServerSideEncryptionWidgets,   true);
    }
    catch {
      encryptionService.serverSideEncryption         =configSystemService.getValueAsBoolean(ConfigSystemParams.SecurityServerSideEncryption,          true);
      encryptionService.serverSideEncryptionDashboard=configSystemService.getValueAsBoolean(ConfigSystemParams.SecurityServerSideEncryptionDashboard, true);
      encryptionService.serverSideEncryptionWidgets  =configSystemService.getValueAsBoolean(ConfigSystemParams.SecurityServerSideEncryptionWidgets,   true);
    }
    finally {
      encryptionService.markConfigLoaded();
    }
  };
}

export function loadVersion(versionService: VersionService) {
  return () => versionService.load();
}

@NgModule({
  declarations: [
    AppComponent,
    PlatformLandingComponent
  ],
  bootstrap: [
    AppComponent
  ],
  imports: [
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    EffectsModule.forRoot([
      ApplicationEffects,
      ApikeyEffects,
      CRMEffects,
      CryptoEffects,
      EcommerceEffects,
      FileManagerEffects,
      InvoiceEffects,
      ProjectEffects,
      RiskIntelligenceEffects,
      TaskEffects,
      TicketEffects,
      TodoEffects
    ]),
    LayoutsModule,
    MsalModule.forRoot(
      new PublicClientApplication(getMsalConfig()),
      {
        authRequest: {
          scopes: ['user.read']
        },
        interactionType: InteractionType.Redirect
      },
      {
        interactionType     : InteractionType.Redirect,
        protectedResourceMap: new Map()
      }
    ),
    NgPipesModule,
    PagesModule,
    StoreDevtoolsModule.instrument({
      maxAge : 25,
      logOnly: environment.production,
    }),
    StoreModule.forRoot(rootReducer),
    ToastsContainer,
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader         : {
        deps      : [HttpClient, VersionService],
        provide   : TranslateLoader,
        useFactory: (createTranslateLoader)
      }
    })
  ],
  providers: [
    { provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: { timezone: 'UTC' } },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor,   multi: true },
    DecimalPipe,
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    {
      deps      : [AuthenticationService, ComponentAccessUserService],
      multi     : true,
      provide   : APP_INITIALIZER,
      useFactory: loadAccessGuids
    },
    {
      deps      : [ConfigSystemService, EncryptionService],
      multi     : true,
      provide   : APP_INITIALIZER,
      useFactory: loadConfigSystem
    },
    {
      deps      : [VersionService],
      multi     : true,
      provide   : APP_INITIALIZER,
      useFactory: loadVersion
    },
  ]
})

export class AppModule {

  constructor(ngbTooltipConfig: NgbTooltipConfig) {
    config({ licenseKey, defaultCurrency: 'GBP' });
    ngbTooltipConfig.closeDelay=0;
    ngbTooltipConfig.container ='body';
    ngbTooltipConfig.openDelay =100;
  }
}
